import type { MilestoneNode } from "pages/design-package-details-page/components/view-dependencies/model";
import { DateUtils } from "utils/dateutils";

export type DesignPackageMilestoneInput = {
  id?: string;
  name: string;
  sequence: number;
  committed_date?: string;
};

export type DesignPackageMilestoneUpdate = {
  milestones: DesignPackageMilestoneInput[];
  deleted_milestone_ids: string[];
};

export type AddMilestonePlacement = {
  isoDate: string;
  prevId: string | null;
  nextId: string | null;
  afterLast: boolean;
  useActualDate: boolean;
  minDate: string | null;
  maxDate: string | null;
  /** Latest actual day on the package. A new committed date may not precede it. */
  latestActualDate: string | null;
};

export type InsertMilestoneChange = {
  name: string;
  prevId: string | null;
  nextId: string | null;
  date: string;
};

export function dayOf(date: string): string {
  return date.slice(0, 10);
}

/** Matches update_design_package_milestones' committed_date shape (midnight). */
export function toCommittedDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return `${dayOf(iso)}T00:00:00`;
}

export function addDaysIso(iso: string, n: number): string {
  const t =
    Date.UTC(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) +
    n * 86_400_000;
  const d = new Date(t);
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${d.getUTCFullYear()}-${m}-${day}`;
}

/**
 * Prefill for a new milestone's committed date: today, or the last actual day
 * when that day is later. The last actual day itself is allowed.
 */
export function defaultNewMilestoneDate(
  latestActualDate: string | null,
  today: string
): string {
  if (latestActualDate && latestActualDate > today) return latestActualDate;
  return today;
}

function clampIso(iso: string, min: string | null, max: string | null): string {
  let next = iso;
  if (min && next < min) next = min;
  if (max && next > max) next = max;
  return next;
}

/**
 * Neighbours of a click date on the design-package axis. Returns null when the
 * day is already occupied. The committed-date floor is the latest actual date
 * only — neighbouring committed dates do not limit it.
 */
export function placementForDate(
  milestones: MilestoneNode[],
  isoDate: string
): AddMilestonePlacement | null {
  const dated = milestones
    .filter((m) => Boolean(m.name) && m.effectiveDate)
    .sort((a, b) => {
      const byDay = dayOf(a.effectiveDate!).localeCompare(
        dayOf(b.effectiveDate!)
      );
      return byDay !== 0 ? byDay : a.sequenceNo - b.sequenceNo;
    });
  const click = dayOf(isoDate);
  if (dated.some((m) => dayOf(m.effectiveDate!) === click)) return null;

  let prev: MilestoneNode | null = null;
  let next: MilestoneNode | null = null;
  for (let i = 0; i < dated.length; i += 1) {
    const m = dated[i];
    const day = dayOf(m.effectiveDate!);
    if (day < click) prev = m;
    else if (day > click) {
      next = m;
      break;
    }
  }

  // A new milestone's committed date cannot land before any actual date already
  // recorded on this design package. The actual day itself is still allowed.
  // Neighbouring committed dates do not set a floor or a ceiling.
  const latestActual = milestones.reduce<string | null>((latest, milestone) => {
    if (!milestone.actualDate) return latest;
    const day = dayOf(milestone.actualDate);
    return !latest || day > latest ? day : latest;
  }, null);

  return {
    isoDate: clampIso(click, latestActual, null),
    prevId: prev?.id ?? null,
    nextId: next?.id ?? null,
    afterLast: !next,
    useActualDate: Boolean(next?.actualDate),
    minDate: latestActual,
    maxDate: null,
    latestActualDate: latestActual
  };
}

function milestoneDay(milestone: MilestoneNode): string | null {
  const raw = milestone.committedDate ?? milestone.effectiveDate;
  return raw ? dayOf(raw) : null;
}

/**
 * Sequence follows committed-date order. The timeline click only opens the
 * dialog; the date the user then picks can land before a later milestone, and
 * the backend rejects a higher sequence number with an earlier committed date.
 */
function applyInsert(
  named: MilestoneNode[],
  insert: InsertMilestoneChange
): MilestoneNode[] {
  const newDay = dayOf(insert.date);
  let insertAt = named.length;
  for (let i = 0; i < named.length; i += 1) {
    const day = milestoneDay(named[i]);
    if (day && newDay < day) {
      insertAt = i;
      break;
    }
  }

  const newNode: MilestoneNode = {
    id: "",
    name: insert.name,
    sequenceNo: 0,
    plannedDate: null,
    committedDate: insert.date,
    actualDate: null,
    effectiveDate: insert.date,
    action: "",
    offset: null
  };

  return [...named.slice(0, insertAt), newNode, ...named.slice(insertAt)];
}

/**
 * Build the update_design_package_milestones payload from the current named
 * milestones. Rename is applied first; delete drops that id and re-sequences
 * the rest. Insert splices a new (id-less) milestone where its committed date
 * falls, then every row is re-sequenced.
 */
export function toDesignPackageMilestoneUpdate(
  milestones: MilestoneNode[],
  change: {
    rename?: { id: string; name: string };
    deleteId?: string;
    insert?: InsertMilestoneChange;
  } = {}
): DesignPackageMilestoneUpdate {
  let remaining = milestones
    .filter((m) => Boolean(m.name) && m.id !== change.deleteId)
    .sort((a, b) => a.sequenceNo - b.sequenceNo)
    .map((m) =>
      change.rename && m.id === change.rename.id
        ? { ...m, name: change.rename.name }
        : m
    );

  if (change.insert) remaining = applyInsert(remaining, change.insert);

  const milestonesPayload: DesignPackageMilestoneInput[] = remaining.map(
    (m, i) => {
      const rawDate = m.committedDate || m.effectiveDate;
      // Existing milestones already store lunch time. Only the new row,
      // which has no id yet, is formatted on the way out.
      const committedDate = m.id
        ? rawDate
        : DateUtils.formatDateWithLunchTime(rawDate ? dayOf(rawDate) : "");
      const row: DesignPackageMilestoneInput = {
        name: m.name,
        sequence: i + 1
      };
      // An undated milestone (it sits in the undated tray) omits the key:
      // the backend rejects committed_date: null as "must be a valid date".
      if (committedDate) row.committed_date = committedDate;
      if (m.id) row.id = m.id;
      return row;
    }
  );

  return {
    milestones: milestonesPayload,
    deleted_milestone_ids: change.deleteId ? [change.deleteId] : []
  };
}
